import { ChangeDetectorRef, Component, input, model, OnInit, output } from '@angular/core';

import { Router } from '@angular/router';

import { translate } from '@jsverse/transloco';
import { DropdownObject } from '@inmoment/the-kitchen';

import { AuthService } from 'app/providers/bridge-api/auth.service';
import { ConfigurationService } from 'app/providers/semantria-api/configuration.service';
import { GroupService } from 'app/providers/semantria-api/group.service';
import { IndustryPackService } from 'app/providers/semantria-api/industry-pack.service';
import { LanguageTemplateService } from 'app/providers/semantria-api/language-template.service';
import {
  EnableClassTelemetry,
  EnableComponentTelemetry,
  injectComponentTelemetryService,
} from 'app/services/otel.service';
import { SentimentUtils } from 'app/utils/sentiment.utils';
import { Utils } from 'app/utils/utils';
import { DocumentViewOptions } from '../../document-view/document-view.component';

@EnableClassTelemetry()
@Component({
  selector: 'app-entity-editor',
  templateUrl: './entity-editor.component.html',
  styleUrls: ['./entity-editor.component.scss'],
  standalone: false,
})
export class EntityEditorComponent implements OnInit {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  readonly options = input<DocumentViewOptions>();
  readonly projectId = input<string>();
  readonly sentimentScale = input<number>();
  readonly entityToEdit = model<any>();
  readonly isLoading = input<boolean>(false);
  readonly configId = input<string>();
  readonly analysisConfigId = input<string>();

  readonly cancel = output<void>();
  readonly delete = output<any>();
  readonly save = output<any>();

  entity: any = {
    sentimentScore: 0.0,
    sentimentPolarity: 'neutral',
    title: '',
    entityType: null,
  };
  public entityTypes: DropdownObject[] = [
    { title: 'Company', id: 'Company' },
    { title: 'Custom', id: 'Custom' },
    { title: 'Job Title', id: 'Job Title' },
    { title: 'Person', id: 'Person' },
    { title: 'Place', id: 'Place' },
    { title: 'Product', id: 'Product' },
    { title: 'Sports', id: 'Sports' },
  ];
  comment: string;

  canEditConfig: boolean;
  deletePressed: boolean;
  sentimentValue: string = 'neutral';

  editConfigButtonTooltip;

  constructor(
    private authService: AuthService,
    private groupService: GroupService,
    private configService: ConfigurationService,
    private industryPackService: IndustryPackService,
    private languageTemplateService: LanguageTemplateService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  async ngOnInit() {
    if (this.entityToEdit()) {
      this.entity = { ...this.entityToEdit() };
      this.sentimentValue = SentimentUtils.getSentimentValueFromRange(
        this.entity.sentimentScore,
        this.options().sentimentRanges.feature,
        this.sentimentScale()
      );
      const entityAlreadyExists = this.entityTypes.find(
        (obj) => obj.title === this.entityToEdit().entityType
      );
      if (!entityAlreadyExists) {
        this.entityTypes.push({
          title: this.entityToEdit().entityType,
          id: this.entityToEdit().entityType,
        });
        this.entityTypes.sort((a, b) => a.title.localeCompare(b.title));
      }

      if (this.analysisConfigId()?.startsWith('ip:')) {
        this.editConfigButtonTooltip = translate(
          'documents.entity-editor.can-not-edit-industry-pack'
        );
      } else if (this.analysisConfigId()?.startsWith('lt:')) {
        this.editConfigButtonTooltip = translate(
          'documents.entity-editor.can-not-edit-language-template'
        );
      } else if (this.analysisConfigId()?.startsWith('cr:')) {
        // Sorry for this, there's no way to check if the underlying config
        // is an IP or an LT or a normal config...
        try {
          await this.industryPackService.getIndustryPack(this.configId());
          this.editConfigButtonTooltip = translate(
            'documents.entity-editor.can-not-edit-route-industry-pack'
          );
        } catch {
          try {
            await this.languageTemplateService.getLanguageTemplate(this.configId());
            this.editConfigButtonTooltip = translate(
              'documents.entity-editor.can-not-edit-route-language-template'
            );
          } catch {
            this.canEditConfig = true;
          }
        }
      } else {
        const config = await this.configService.getConfiguration(this.configId());
        if (config.layer && this.entity.type === 'named') {
          this.canEditConfig = false;
          this.editConfigButtonTooltip = translate(
            'documents.entity-editor.definition-not-available'
          );
        } else {
          const session = await this.authService.getSession();
          this.canEditConfig = this.authService.canEditConfiguration(session, this.configId());
          if (!this.canEditConfig) {
            this.editConfigButtonTooltip = translate('documents.entity-editor.no-permission');
          }
        }
      }
    } else {
      this.entityToEdit.set({ id: '', title: '', entityType: null, sentimentScore: 0.0 });
    }
    this.cdr.detectChanges();
  }

  hasChanges(): boolean {
    return (
      this.entity.entityType !== this.entityToEdit().entityType ||
      this.entity.title !== this.entityToEdit().title ||
      (this.entityToEdit() &&
        Utils.roundNearest(this.entity.sentimentScore) !==
          Utils.roundNearest(this.entityToEdit().sentimentScore))
    );
  }

  formValid(): boolean {
    return this.entity.entityType && this.entity.title && this.entity.title.trim() !== '';
  }

  onSentimentChanged($event) {
    this.entity.sentimentScore = $event.score;
    this.entity.sentimentPolarity = $event.value;
  }

  editConfig(fragment) {
    const queryParams = {} as any;

    if (this.entity.id) {
      queryParams.itemName = this.entity.title;
      queryParams.itemType = this.entity.entityType;
      queryParams.entityType = this.entity.type;
    }
    const configsUrl = this.router.createUrlTree(
      ['groups', this.groupService.getSelectedGroupId(true), 'configurations', this.configId()],
      {
        queryParams,
        fragment,
      }
    );

    window.open(this.router.serializeUrl(configsUrl));
  }

  cancelEntity() {
    if (this.deletePressed) {
      this.deletePressed = false;
    } else {
      this.cancel.emit();
    }
  }

  deleteEntity() {
    if (this.deletePressed) {
      this.delete.emit({ ...this.entity, comment: this.comment });
    } else {
      this.deletePressed = true;
    }
  }

  saveEntity() {
    this.save.emit({ ...this.entity, comment: this.comment });
  }
}
